import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { AddressType } from '@sg/garnish';
import { switchcase } from '@sg/garnish';
import { FlattenedOrderStatuses } from '@sg/graphql-schema';

import { getDayDateTimeYear, getOrderTimeRange, ignoreTimezone } from '@order/utils';

import { useDayLabel } from '../../../../hooks/useDayLabel';
import { getOrderTime } from '../../../../utils';

// ────────────────────────────────────────────────────────────────────────────────

export const useOrderStatusDetails = (params: UseOrderStatusDetailsParams) => {
  const {
    orderStatus,
    orderType,
    isPast,
    isTrackable,
    wantedTime,
    estimatedDeliveryTime = '',
    locationName: name,
    locationAddress: address,
  } = params;

  const { startTime, endTime, timeEstimate } = useMemo(() => {
    const orderTime = getOrderTime({
      wantedTime,
      deliveryOrderDetail: { estimatedDeliveryTime },
    });
    const estimate = orderTime ? ignoreTimezone(orderTime) : undefined;

    return { ...getOrderTimeRange(estimate), timeEstimate: estimate };
  }, [wantedTime, estimatedDeliveryTime]);

  const isFailed = orderStatus === FlattenedOrderStatuses.Failed;

  const { formatMessage } = useIntl();

  const formatDateMessage = (key: string) =>
    formatMessage(dateMessageMap[key as keyof typeof dateMessageMap]);

  const {
    date = '',
    time = '',
    year = '',
  } = timeEstimate ? getDayDateTimeYear(formatDateMessage, timeEstimate) : {};

  const day = useDayLabel(timeEstimate);

  const localizationValues = {
    name,
    address,
    day,
    date,
    time,
    start_time: startTime,
    end_time: endTime,
    year,
    order_type: orderType,
  };

  if (orderType === 'in-store') {
    return formatMessage(messages.subtitlePickupCompleted, localizationValues);
  }

  if (isFailed) {
    return formatMessage(messages.subtitleFailed, localizationValues);
  }

  if (isPast) {
    return formatMessage(messages.subtitlePast, localizationValues);
  }

  if (!isTrackable) {
    return formatMessage(messages.subtitleUnavailable, localizationValues);
  }

  if (orderType === 'pickup') {
    return switchcase({
      [FlattenedOrderStatuses.Received]: formatMessage(
        messages.subtitlePickupReceived,
        localizationValues,
      ),
      [FlattenedOrderStatuses.Preparing]: formatMessage(
        messages.subtitlePickupPreparing,
        localizationValues,
      ),
      [FlattenedOrderStatuses.Completed]: formatMessage(
        messages.subtitlePickupCompleted,
        localizationValues,
      ),
    })(formatMessage(messages.subtitleUnavailable, localizationValues))(
      orderStatus ?? FlattenedOrderStatuses.Received,
    );
  }

  if (orderType === 'outpost') {
    return switchcase({
      [FlattenedOrderStatuses.Received]: formatMessage(
        messages.subtitleOutpostReceived,
        localizationValues,
      ),
      [FlattenedOrderStatuses.Delivering]: formatMessage(
        messages.subtitleOutpostDelivering,
        localizationValues,
      ),
      [FlattenedOrderStatuses.Completed]: formatMessage(
        messages.subtitleOutpostCompleted,
        localizationValues,
      ),
    })(formatMessage(messages.subtitleUnavailable, localizationValues))(
      orderStatus ?? FlattenedOrderStatuses.Received,
    );
  }

  if (orderType === 'delivery') {
    return switchcase({
      [FlattenedOrderStatuses.Received]: formatMessage(
        messages.subtitleDeliveryReceived,
        localizationValues,
      ),
      [FlattenedOrderStatuses.Preparing]: formatMessage(
        messages.subtitleDeliveryPreparing,
        localizationValues,
      ),
      [FlattenedOrderStatuses.ReadyForCourier]: formatMessage(
        messages.subtitleDeliveryCourier,
        localizationValues,
      ),
      [FlattenedOrderStatuses.Delivering]: formatMessage(
        messages.subtitleDeliveryRoute,
        localizationValues,
      ),
      [FlattenedOrderStatuses.Completed]: formatMessage(
        messages.subtitleDeliveryCompleted,
        localizationValues,
      ),
    })(formatMessage(messages.subtitleUnavailable, localizationValues))(
      orderStatus ?? FlattenedOrderStatuses.Received,
    );
  }

  return formatMessage(messages.subtitleUnavailable, localizationValues);
};

// ─── Types ──────────────────────────────────────────────────────────────────────

export type UseOrderStatusDetailsParams = {
  wantedTime?: string;
  estimatedDeliveryTime?: string;
  locationAddress?: string;
  locationName?: string;
  orderType?: AddressType;
  orderStatus?: FlattenedOrderStatuses;
  isTrackable: boolean;
  isPast: boolean;
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  subtitlePickupCompleted: {
    defaultMessage: 'Your order was completed {day} at {time}',
    description: 'Order Status | Info | Subtitle for completed pickup',
  },
  subtitleFailed: {
    defaultMessage:
      "We're so sorry we had to cancel your order, there was a snag with it. We are processing a full refund, which will post in your account within 3-5 business days.",
    description: 'Order Status | Info | Subtitle for failed order',
  },
  subtitlePast: {
    defaultMessage: 'Your order was completed {day}, {date} at {time}',
    description: 'Order Status | Info | Subtitle for past order',
  },
  subtitleUnavailable: {
    defaultMessage:
      '{order_type, select, pickup {Your pickup order is awaiting preparation.} outpost {Your outpost order is awaiting delivery.} delivery {Your delivery order is being prepared.} other {}}',
    description: 'Order Status | Info | Subtitle for unavailable order',
  },
  subtitlePickupReceived: {
    defaultMessage: 'Scheduled pickup time at {name} is {day} at {time}',
    description: 'Order Status | Info | Subtitle for received pickup order',
  },
  subtitlePickupPreparing: {
    defaultMessage: 'Scheduled pickup time at {name} is {day} at {time}',
    description: 'Order Status | Info | Subtitle for preparing pickup order',
  },
  subtitleOutpostReceived: {
    defaultMessage: 'Estimated delivery {day} between {start_time}-{end_time}',
    description: 'Order Status | Info | Subtitle for received outpost order',
  },
  subtitleOutpostDelivering: {
    defaultMessage: 'Estimated delivery {day} between {start_time}-{end_time}',
    description: 'Order Status | Info | Subtitle for delivering outpost order',
  },
  subtitleOutpostCompleted: {
    defaultMessage: 'Your order was completed {day} at {time}',
    description: 'Order Status | Info | Subtitle for completed outpost order',
  },
  subtitleDeliveryReceived: {
    defaultMessage: 'Scheduled delivery is {day} {start_time}-{end_time}',
    description: 'Order Status | Info | Subtitle for received delivery order',
  },
  subtitleDeliveryPreparing: {
    defaultMessage: 'Estimated delivery is {day} {start_time}-{end_time}',
    description: 'Order Status | Info | Subtitle for preparing delivery order',
  },
  subtitleDeliveryCourier: {
    defaultMessage: 'Estimated delivery is {day} {start_time}-{end_time}',
    description: 'Order Status | Info | Subtitle for ready for courier delivery order',
  },
  subtitleDeliveryRoute: {
    defaultMessage: 'Estimated delivery is {day} {start_time}-{end_time}',
    description: 'Order Status | Info | Subtitle for delivering delivery order',
  },
  subtitleDeliveryCompleted: {
    defaultMessage: 'Your order was completed {day} at {time}',
    description: 'Order Status | Info | Subtitle for completed delivery order',
  },
});

const dateMessages = defineMessages({
  weekdaySunday: { defaultMessage: 'Sunday', description: 'Order Status | Weekday | Sunday' },
  weekdayMonday: { defaultMessage: 'Monday', description: 'Order Status | Weekday | Monday' },
  weekdayTuesday: { defaultMessage: 'Tuesday', description: 'Order Status | Weekday | Tuesday' },
  weekdayWednesday: {
    defaultMessage: 'Wednesday',
    description: 'Order Status | Weekday | Wednesday',
  },
  weekdayThursday: { defaultMessage: 'Thursday', description: 'Order Status | Weekday | Thursday' },
  weekdayFriday: { defaultMessage: 'Friday', description: 'Order Status | Weekday | Friday' },
  weekdaySaturday: { defaultMessage: 'Saturday', description: 'Order Status | Weekday | Saturday' },
  monthJanuary: { defaultMessage: 'January', description: 'Order Status | Month | January' },
  monthFebruary: { defaultMessage: 'February', description: 'Order Status | Month | February' },
  monthMarch: { defaultMessage: 'March', description: 'Order Status | Month | March' },
  monthApril: { defaultMessage: 'April', description: 'Order Status | Month | April' },
  monthMay: { defaultMessage: 'May', description: 'Order Status | Month | May' },
  monthJune: { defaultMessage: 'June', description: 'Order Status | Month | June' },
  monthJuly: { defaultMessage: 'July', description: 'Order Status | Month | July' },
  monthAugust: { defaultMessage: 'August', description: 'Order Status | Month | August' },
  monthSeptember: { defaultMessage: 'September', description: 'Order Status | Month | September' },
  monthOctober: { defaultMessage: 'October', description: 'Order Status | Month | October' },
  monthNovember: { defaultMessage: 'November', description: 'Order Status | Month | November' },
  monthDecember: { defaultMessage: 'December', description: 'Order Status | Month | December' },
});

const dateMessageMap = {
  'general.weekday.sunday': dateMessages.weekdaySunday,
  'general.weekday.monday': dateMessages.weekdayMonday,
  'general.weekday.tuesday': dateMessages.weekdayTuesday,
  'general.weekday.wednesday': dateMessages.weekdayWednesday,
  'general.weekday.thursday': dateMessages.weekdayThursday,
  'general.weekday.friday': dateMessages.weekdayFriday,
  'general.weekday.saturday': dateMessages.weekdaySaturday,
  'general.month.january': dateMessages.monthJanuary,
  'general.month.february': dateMessages.monthFebruary,
  'general.month.march': dateMessages.monthMarch,
  'general.month.april': dateMessages.monthApril,
  'general.month.may': dateMessages.monthMay,
  'general.month.june': dateMessages.monthJune,
  'general.month.july': dateMessages.monthJuly,
  'general.month.august': dateMessages.monthAugust,
  'general.month.september': dateMessages.monthSeptember,
  'general.month.october': dateMessages.monthOctober,
  'general.month.november': dateMessages.monthNovember,
  'general.month.december': dateMessages.monthDecember,
} as const;
